'use client'

import { apiKeyClient } from '@better-auth/api-key/client'
import type { AuthProviderProps } from '@saas-ui/auth-provider'
// `ClientOptions` was renamed to `BetterAuthClientOptions` in Better Auth 1.6;
// `User` is still exported from the package root.
import type { BetterAuthClientOptions, User } from 'better-auth'
import {
  adminClient,
  deviceAuthorizationClient,
  genericOAuthClient,
} from 'better-auth/client/plugins'
import { createAuthClient as createBetterAuthClient } from 'better-auth/react'
import type { SocialProvider } from 'better-auth/social-providers'

// The plugin tuple is fixed and must stay a literal tuple: Better Auth infers
// the admin actions, session `$Infer`, and account shapes from the exact plugin
// list via `InferClientPlugins`/`$InferServerPlugin`. Spreading a widened
// `BetterAuthClientPlugin[]` (as the old `options.plugins` passthrough did)
// collapses that tuple to a union array, which in 1.6 degrades the inferred
// client to its base type — admin `impersonateUser`/`stopImpersonating` vanish,
// `useSession` data becomes `never`, and `listAccounts` loses `accountId`. No
// caller supplies extra plugins, so we omit that option entirely to keep the
// tuple precise rather than silently dropping a passed-in `plugins` array.
export function createAuthClient(
  options: Omit<BetterAuthClientOptions, 'plugins'>,
) {
  return createBetterAuthClient({
    baseURL: 'http://localhost:3000',
    ...options,
    plugins: [
      adminClient(),
      // `apiKey` moved into the standalone `@better-auth/api-key` package in 1.6.
      apiKeyClient(),
      genericOAuthClient(),
      // Client counterpart for the Device Authorization plugin wired up in
      // `auth.ts`; exposes `device` endpoints so the future device flow (and its
      // integration tests) can drive request → approve → poll from the client.
      deviceAuthorizationClient(),
    ],
  })
}

export function createAuthService(client: ReturnType<typeof createAuthClient>) {
  const authProviderProps = {
    onLogin: async (
      params: {
        provider?: string
        email?: string
        password?: string
      },
      options?: {
        redirectTo?: string
      },
    ) => {
      const redirectTo = options?.redirectTo ?? '/'

      if (params.provider) {
        await client.signIn.oauth2({
          providerId: params.provider,
          callbackURL: redirectTo,
        })
        return null
      } else if (params.email && params.password) {
        const { data, error } = await client.signIn.email({
          email: params.email,
          password: params.password,
          callbackURL: redirectTo,
        })

        if (error) {
          throw new Error('Invalid email or password', {
            cause: error,
          })
        }

        return data.user
      }

      throw new Error('Invalid parameters')
    },
    onSignup: async (
      params: {
        provider?: string
        name?: string
        email?: string
        password?: string
        inviteCode?: string
      },
      options?: {
        redirectTo?: string
      },
    ) => {
      const redirectTo = options?.redirectTo ?? '/'

      if (params.provider) {
        await client.signIn.social({
          provider: params.provider as SocialProvider,
          callbackURL: redirectTo,
        })
        return null
      } else if (params.email && params.password) {
        const { error } = await client.signUp.email({
          email: params.email,
          name: params.name ?? '',
          password: params.password,
          callbackURL: redirectTo,
          fetchOptions: params.inviteCode
            ? {
                headers: {
                  'x-invite-code': params.inviteCode,
                },
              }
            : undefined,
        })

        if (error) {
          throw new Error('Invalid email or password', {
            cause: error,
          })
        }

        // If there is no error, we can assume the user is signed up
        // auto login is enabled by default, so the user will be redirected
        // to the dashboard.
        return
      }

      throw new Error('Invalid parameters')
    },
    onResetPassword: async (
      params: { email: string },
      options?: {
        redirectTo?: string
      },
    ) => {
      const { data, error } = await client.requestPasswordReset({
        email: params.email,
        redirectTo: options?.redirectTo,
      })

      if (error) {
        throw new Error('Could not send reset email', {
          cause: error,
        })
      }

      return data
    },
    onUpdatePassword: async (params) => {
      if (params.token) {
        const { error } = await client.resetPassword({
          newPassword: params.password,
          token: params.token,
        })

        if (error) {
          const message =
            error.code === 'INVALID_TOKEN'
              ? 'Token is invalid or expired'
              : 'Could not reset password'

          throw new Error(message, {
            cause: error,
          })
        }
      } else {
        const { error } = await client.changePassword({
          currentPassword: params.password,
          newPassword: params.newPassword,
        })

        if (error) {
          if (error.code === 'INVALID_PASSWORD') {
            throw new Error('Invalid current password', {
              cause: error,
            })
          }

          throw new Error(error.message, {
            cause: error,
          })
        }
      }
    },
    onLogout: async () => {
      return await client.signOut()
    },
    onLoadUser: async () => {
      const session = await client.getSession()
      return session?.data?.user ?? null
    },
  } satisfies AuthProviderProps<User>

  return authProviderProps
}
